import { resolveLocalRequest, shouldForce401 } from './localRequestUtils';
import { createCombinedAbortSignal } from './abortSignalUtils';
import { getAvroGateway, ChirpInternalError } from './chirp-avro';
/** Fires a hub-http POST. Callers handle the rejected non-2xx `errorResponse`. */
export function sendHubHttpRequest(args) {
  return args.hubHttpClient.post(args.gatewayUrl, Object.assign({
    data: args.request,
    headers: args.headers,
    timeout: args.clientTimeout,
    query: {
      clienttimeout: String(args.clientTimeout)
    },
    environment: args.environment,
    hublet: args.hublet
  }, args.portalId && args.auth === 'app' && args.calledFrom !== 'npmShim' ? {
    portalId: args.portalId
  } : {}));
}

/** Fires a chirp fetch POST and returns the raw `Response`. Callers check status. */
export function sendChirpFetchRequest(args) {
  var _args$fetcherOverride;
  if (!args.fetcherOverride && !args.rpcFetcherFactory) {
    return Promise.reject(new Error('Either hubHttpClient, fetcherOverride, or rpcFetcherFactory is required'));
  }
  const fetcher = (_args$fetcherOverride = args.fetcherOverride) !== null && _args$fetcherOverride !== void 0 ? _args$fetcherOverride : args.rpcFetcherFactory(args.auth, args.environment, args.hublet);
  const signal = createCombinedAbortSignal(args.clientTimeout);
  return fetcher(`${args.gatewayUrl}?clienttimeout=${args.clientTimeout}`, {
    method: 'POST',
    headers: Object.assign({
      'Content-Type': 'application/json'
    }, args.headers),
    body: JSON.stringify(args.request),
    signal
  });
}
function makeHubHttpRequest(args) {
  return sendHubHttpRequest(args).catch(errorResponse => {
    if (errorResponse.status !== 0 && 'responseJSON' in errorResponse) {
      const isAuthError = errorResponse.status === 401;
      return Object.assign({}, isAuthError ? {
        isAuthError: true
      } : {}, errorResponse.responseJSON);
    }
    throw errorResponse;
  });
}
function makeChirpFetchRequest(args) {
  return sendChirpFetchRequest(args).then(response => {
    if (response.status === 401 || shouldForce401()) {
      throw Object.assign(new ChirpInternalError('Unauthorized'), {
        isAuthError: true,
        serviceName: args.serviceName
      });
    }
    return response.json();
  });
}

/**
 * Merges identity-derived headers (`X-Origin-HubId`, `X-Origin-UserId`) into
 * `args.headers` and returns the updated args. Pure/synchronous — no I/O.
 */
export function constructRequest(args) {
  const headers = Object.assign({}, args.headers);
  if (args.portalId !== undefined && (args.auth === 'internal' || args.calledFrom === 'npmShim')) {
    headers['X-Origin-HubId'] = String(args.portalId);
  }
  if (args.userId !== undefined) {
    headers['X-Origin-UserId'] = String(args.userId);
  }
  return Object.assign({}, args, {
    headers
  });
}

/**
 * Sends a fully-constructed request (headers already merged) via the
 * appropriate transport: `hubHttpClient` when present, otherwise the
 * fetch-based chirp transport.
 */
export function sendRequest(args) {
  return args.hubHttpClient ? makeHubHttpRequest(Object.assign({}, args, {
    hubHttpClient: args.hubHttpClient
  })) : makeChirpFetchRequest(args);
}

/** Constructs and sends an RPC request. Equivalent to `sendRequest(constructRequest(args))`. */
export function dispatchRpcRequest(args) {
  return sendRequest(constructRequest(args));
}
export async function makeChirpAvroCall({
  hubHttpClient,
  details,
  responseParser,
  inputs,
  alternateData,
  calledFrom = 'bend',
  rpcFetcherFactory,
  fetcherOverride
}) {
  var _inputs$request, _ref;
  const {
    timeout,
    portalId,
    userId,
    environment,
    hublet,
    explicitLocalWorkspace
  } = inputs !== null && inputs !== void 0 ? inputs : {};
  const request = (_inputs$request = inputs === null || inputs === void 0 ? void 0 : inputs.request) !== null && _inputs$request !== void 0 ? _inputs$request : {};
  const {
    auth,
    serviceName,
    rpcName,
    fingerprint,
    na1Only,
    isHublessAppAuth,
    routingLocality,
    timeout: defaultTimeout
  } = details;
  const syncStackError = new Error(`CHIRP RPC failed for ${rpcName}`);
  const clientTimeout = typeof timeout === 'number' ? timeout : defaultTimeout !== null && defaultTimeout !== void 0 ? defaultTimeout : 5000;
  const localService = await resolveLocalRequest(serviceName, explicitLocalWorkspace);
  const shouldMakeLocalRequest = localService !== null;
  const headers = {};
  if (fingerprint !== undefined) {
    headers['x-hs-fingerprint'] = fingerprint;
  }
  const gatewayUrl = `${getAvroGateway({
    auth,
    timeout: clientTimeout,
    isLocal: shouldMakeLocalRequest,
    na1Only,
    isHublessAppAuth,
    isRoutedGlobally: routingLocality === 'GLOBAL',
    localWorkspace: localService === null || localService === void 0 ? void 0 : localService.workspace,
    hublet
  })}/${serviceName}/${rpcName}`;
  const data = (_ref = shouldMakeLocalRequest ? undefined : alternateData) !== null && _ref !== void 0 ? _ref : dispatchRpcRequest({
    gatewayUrl,
    request,
    headers,
    auth,
    clientTimeout,
    environment,
    hublet,
    portalId,
    userId,
    calledFrom,
    hubHttpClient,
    rpcFetcherFactory,
    fetcherOverride,
    serviceName
  });
  return data.then(response => responseParser(response, serviceName)).catch(error => {
    syncStackError.cause = error;
    throw syncStackError;
  });
}